import classNames from "classnames";
import React from "react";
import { downloadFile } from "utils/file";
import { getFileNameFromS3Url } from "utils/util";
import Button from "./Button";

export interface CellData {
  title: string;
  value: string | string[] | React.ReactNode;
  isTypeFile?: boolean;
  columnWidth?: number;
  cellClassName?: string;
  cellTitleClassName?: string;
}

interface TableSection {
  type: "table";
  header: {
    title: string;
    columnWidth?: number;
    cellClassName?: string;
  }[];
  body: {
    value: string | React.ReactNode;
    columnWidth?: number;
    cellClassName?: string;
  }[][];
}

const isTableSection = (item: CellData[] | TableSection): item is TableSection => {
  return typeof item === "object" && !Array.isArray(item) && "type" in item && item.type === "table";
};

interface DataTableTableProps {
  data: (CellData[] | TableSection)[];
  isDisabled?: boolean;
}

const DataTable: React.FC<DataTableTableProps> = ({ data, isDisabled }) => {
  if (!data.length) {
    return <></>;
  }

  return (
    <div className="p-3">
      {data.map((row, rowIndex) => {
        if (isTableSection(row)) {
          const table = row;
          return (
            <div key={rowIndex} className={classNames("flex flex-wrap")}>
              {/* Header */}
              {table.header.map((headerCell, cellIndex) => (
                <div
                  key={cellIndex}
                  className={classNames(
                    "text-left text-xs font-medium text-gray-700 uppercase tracking-wider",
                    `md:w-1/${headerCell.columnWidth}`
                  )}
                >
                  {headerCell.title}
                </div>
              ))}

              {/* Body Rows */}
              {table.body.map((bodyRow, bodyRowIndex) => (
                <div
                  key={bodyRowIndex}
                  className="flex flex-wrap w-full py-1"
                >
                  {bodyRow.map((cell, cellIndex) => (
                    <div
                      key={cellIndex}
                      className={classNames(
                        `md:w-1/${cell.columnWidth}`
                      )}
                    >
                      <p className="text-sm leading-5 font-medium text-gray-900 break-words">
                        {cell.value || "-"}
                      </p>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          );
        }
        
        return (
          <div key={rowIndex} className={classNames("flex flex-wrap")}>
            {row.map((cell, cellIndex) => (
              <div
                key={cellIndex}
                className={classNames(
                  "w-1/2 mt-3 md:mt-0 md:pr-8 mb-5",
                  row.length === 1 && "w-full",
                  row.length > 1 &&
                    `md:w-1/${cell.columnWidth ? cell.columnWidth : row.length}`,
                  cell.cellClassName
                )}
              >
                <h3
                  className={classNames(
                    "text-xs leading-4 font-medium tracking-wider uppercase text-gray-700",
                    isDisabled && "!text-gray-400",
                    cell.cellTitleClassName
                  )}
                >
                  {cell.title}
                </h3>
                {cell.isTypeFile ? (
                  <div className="flex flex-col">
                    {(cell.value as string[]).map((fileName, fileIndex) => (
                      <Button
                        variant="icon"
                        key={fileIndex}
                        onClick={() => downloadFile(fileName)}
                        className={classNames(
                          "text-primary-600 underline text-sm font-bold text-start mb-1 break-words"
                        )}
                        type="button"
                        size="noSize"
                        disabled={isDisabled}
                      >
                        {getFileNameFromS3Url(fileName)}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p
                    className={classNames(
                      "text-sm leading-5 font-medium text-gray-900 break-words",
                      isDisabled && "!text-gray-400"
                    )}
                  >
                    {cell.value}
                  </p>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default DataTable;
